import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";

const TIPOS_MASAJE = [
  { value: "masaje_relajante", label: "Masaje relajante" },
  { value: "masaje_deportivo", label: "Masaje deportivo" },
  { value: "masaje_terapeutico", label: "Masaje terapéutico" },
  { value: "quiromasaje", label: "Quiromasaje" },
  { value: "drenaje_linfatico", label: "Drenaje linfático" },
  { value: "reflexologia", label: "Reflexología" },
  { value: "shiatsu", label: "Shiatsu" },
  { value: "yoga", label: "Yoga" },
  { value: "meditacion", label: "Meditación" },
  { value: "reiki", label: "Reiki" },
];

// El backend usa los valores historicos del catalogo para relajante y deportivo.
const TIPO_A_COLUMNA: Record<string, string> = {
  masaje_relajante: "relajante",
  masaje_deportivo: "deportivo",
};

const IDIOMAS = ["español", "catalán", "inglés", "francés", "alemán", "italiano", "portugués"];

const UBICACIONES = [
  { value: "domicilio", label: "A domicilio" },
  { value: "consultorio", label: "En consultorio" },
  { value: "ambos", label: "Ambos" },
];

const solicitudSchema = z.object({
  nombre: z.string().trim().min(1, "El nombre es obligatorio").max(80),
  apellidos: z.string().trim().max(120),
  email: z.string().trim().email("Correo no válido").max(255),
  telefono: z.string().trim().max(30),
  ciudad: z.string().trim().min(1, "La ciudad es obligatoria").max(80),
  tipos_masaje: z.array(z.string()).min(1, "Elige al menos una terapia"),
  idiomas: z.array(z.string()),
  ubicacion_servicio: z.enum(["domicilio", "consultorio", "ambos"]),
  anos_experiencia: z.string().trim().max(3),
  precio_desde: z.string().trim().max(7),
  precio_hasta: z.string().trim().max(7),
  bio: z.string().trim().max(1500),
});

type FormState = z.infer<typeof solicitudSchema>;

const initialForm: FormState = {
  nombre: "",
  apellidos: "",
  email: "",
  telefono: "",
  ciudad: "",
  tipos_masaje: [],
  idiomas: [],
  ubicacion_servicio: "ambos",
  anos_experiencia: "",
  precio_desde: "",
  precio_hasta: "",
  bio: "",
};

const Chip = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`rounded-full px-3 py-1.5 text-xs transition-colors ${
      active
        ? "bg-primary text-primary-foreground"
        : "bg-muted text-muted-foreground hover:text-foreground"
    }`}
  >
    {children}
  </button>
);

const SoyTerapeuta = () => {
  const { user, loading, roles, roleLoading } = useAuth();
  const [form, setForm] = useState<FormState>(initialForm);
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [errorEnvio, setErrorEnvio] = useState("");

  useEffect(() => {
    if (user?.email) setForm((f) => (f.email ? f : { ...f, email: user.email as string }));
  }, [user]);

  const yaEsTerapeuta = roles.includes("terapeuta");

  const toggle = (campo: "tipos_masaje" | "idiomas", valor: string) =>
    setForm((f) => ({
      ...f,
      [campo]: f[campo].includes(valor)
        ? f[campo].filter((v) => v !== valor)
        : [...f[campo], valor],
    }));

  const set = (campo: keyof FormState, valor: string) =>
    setForm((f) => ({ ...f, [campo]: valor }));

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorEnvio("");
    const parsed = solicitudSchema.safeParse(form);
    if (!parsed.success) {
      const mapa: Record<string, string> = {};
      parsed.error.issues.forEach((i) => {
        mapa[String(i.path[0])] = i.message;
      });
      setErrores(mapa);
      return;
    }
    setErrores({});
    if (!user) return;

    setEnviando(true);
    const v = parsed.data;
    // No enviamos `status`: el servidor fuerza siempre "pendiente" en las altas nuevas.
    const { error } = await supabase.from("profiles").insert({
      user_id: user.id,
      nombre: v.nombre,
      apellidos: v.apellidos || null,
      email: v.email,
      telefono: v.telefono || null,
      ciudad: v.ciudad,
      tipos_masaje: v.tipos_masaje.map((t) => TIPO_A_COLUMNA[t] ?? t) as never,
      idiomas: v.idiomas.length > 0 ? v.idiomas : null,
      ubicacion_servicio: v.ubicacion_servicio,
      anos_experiencia: v.anos_experiencia ? Number(v.anos_experiencia) : null,
      precio_desde: v.precio_desde ? Number(v.precio_desde) : null,
      precio_hasta: v.precio_hasta ? Number(v.precio_hasta) : null,
      bio: v.bio || null,
    });
    setEnviando(false);

    if (error) {
      setErrorEnvio("No hemos podido enviar la solicitud. Inténtalo de nuevo en unos minutos.");
      return;
    }
    setEnviado(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Trabaja con nosotros como terapeuta"
        description="Únete a MassHappy como terapeuta verificado. Sin cuota de entrada y sin permanencia. Empezamos por Barcelona."
        url="https://masshappy.com/soy-terapeuta"
      />

      <main className="container mx-auto max-w-2xl px-4 py-14 md:py-20">
        <header className="text-center">
          <h1 className="font-serif text-3xl md:text-5xl font-semibold leading-tight text-foreground">
            Trabaja con nosotros
          </h1>
          <p className="mt-4 text-sm md:text-base text-muted-foreground leading-relaxed">
            Estamos incorporando a los primeros terapeutas de Barcelona. Sin cuota de entrada y sin permanencia.
          </p>
        </header>

        <ul className="mt-10 space-y-3">
          {[
            "Perfil verificado, con tu foto, tus terapias y tus precios",
            "Clientes que ya están en la app buscando acompañamiento",
            "Tú decides tu disponibilidad y tus tarifas",
          ].map((punto) => (
            <li
              key={punto}
              className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground shadow-soft"
            >
              {punto}
            </li>
          ))}
        </ul>

        <section className="mt-12">
          {(loading || roleLoading) && (
            <div className="h-32 animate-pulse rounded-xl bg-muted" />
          )}

          {!loading && !roleLoading && !user && (
            <div className="rounded-xl border border-border bg-card p-6 text-center shadow-soft">
              <p className="text-sm text-muted-foreground">
                Necesitas una cuenta para enviar tu solicitud.
              </p>
              <div className="mt-5">
                <Button asChild size="lg">
                  <Link to="/acceso">Entrar para solicitar</Link>
                </Button>
              </div>
            </div>
          )}

          {!loading && !roleLoading && user && yaEsTerapeuta && (
            <div className="rounded-xl border border-border bg-card p-6 text-center shadow-soft">
              <h2 className="font-serif text-xl text-foreground">Ya tienes una solicitud enviada</h2>
              <div className="mt-5">
                <Button asChild>
                  <Link to="/negocio">Ir a mi negocio</Link>
                </Button>
              </div>
            </div>
          )}

          {!loading && !roleLoading && user && !yaEsTerapeuta && enviado && (
            <div className="rounded-xl border border-border bg-card p-6 text-center shadow-soft">
              <h2 className="font-serif text-xl text-foreground">Solicitud enviada</h2>
              <p className="mt-3 text-sm text-muted-foreground">
                La revisamos a mano y te escribimos en unos días.
              </p>
            </div>
          )}

          {!loading && !roleLoading && user && !yaEsTerapeuta && !enviado && (
            <form onSubmit={enviar} className="space-y-6" noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="nombre">Nombre</Label>
                  <Input
                    id="nombre"
                    value={form.nombre}
                    maxLength={80}
                    onChange={(e) => set("nombre", e.target.value)}
                    className="mt-1.5"
                  />
                  {errores.nombre && (
                    <p className="mt-1 text-xs text-destructive">{errores.nombre}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="apellidos">Apellidos</Label>
                  <Input
                    id="apellidos"
                    value={form.apellidos}
                    maxLength={120}
                    onChange={(e) => set("apellidos", e.target.value)}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Correo</Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    maxLength={255}
                    onChange={(e) => set("email", e.target.value)}
                    className="mt-1.5"
                  />
                  {errores.email && (
                    <p className="mt-1 text-xs text-destructive">{errores.email}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="telefono">Teléfono</Label>
                  <Input
                    id="telefono"
                    value={form.telefono}
                    maxLength={30}
                    onChange={(e) => set("telefono", e.target.value)}
                    className="mt-1.5"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="ciudad">Ciudad</Label>
                  <Input
                    id="ciudad"
                    value={form.ciudad}
                    maxLength={80}
                    onChange={(e) => set("ciudad", e.target.value)}
                    className="mt-1.5"
                  />
                  {errores.ciudad && (
                    <p className="mt-1 text-xs text-destructive">{errores.ciudad}</p>
                  )}
                </div>
              </div>

              <fieldset>
                <legend className="text-sm font-medium text-foreground">Terapias que ofreces</legend>
                <div className="mt-3 flex flex-wrap gap-2">
                  {TIPOS_MASAJE.map((t) => (
                    <Chip
                      key={t.value}
                      active={form.tipos_masaje.includes(t.value)}
                      onClick={() => toggle("tipos_masaje", t.value)}
                    >
                      {t.label}
                    </Chip>
                  ))}
                </div>
                {errores.tipos_masaje && (
                  <p className="mt-2 text-xs text-destructive">{errores.tipos_masaje}</p>
                )}
              </fieldset>

              <fieldset>
                <legend className="text-sm font-medium text-foreground">Idiomas</legend>
                <div className="mt-3 flex flex-wrap gap-2">
                  {IDIOMAS.map((idioma) => (
                    <Chip
                      key={idioma}
                      active={form.idiomas.includes(idioma)}
                      onClick={() => toggle("idiomas", idioma)}
                    >
                      {idioma}
                    </Chip>
                  ))}
                </div>
              </fieldset>

              <fieldset>
                <legend className="text-sm font-medium text-foreground">Dónde atiendes</legend>
                <div className="mt-3 flex flex-wrap gap-2">
                  {UBICACIONES.map((u) => (
                    <Chip
                      key={u.value}
                      active={form.ubicacion_servicio === u.value}
                      onClick={() => set("ubicacion_servicio", u.value)}
                    >
                      {u.label}
                    </Chip>
                  ))}
                </div>
              </fieldset>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="anos">Años de experiencia</Label>
                  <Input
                    id="anos"
                    type="number"
                    min={0}
                    max={80}
                    value={form.anos_experiencia}
                    onChange={(e) => set("anos_experiencia", e.target.value)}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="desde">Precio desde</Label>
                  <Input
                    id="desde"
                    type="number"
                    min={0}
                    value={form.precio_desde}
                    onChange={(e) => set("precio_desde", e.target.value)}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="hasta">Precio hasta</Label>
                  <Input
                    id="hasta"
                    type="number"
                    min={0}
                    value={form.precio_hasta}
                    onChange={(e) => set("precio_hasta", e.target.value)}
                    className="mt-1.5"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="bio">Cuéntanos quién eres y cómo trabajas</Label>
                <Textarea
                  id="bio"
                  rows={5}
                  maxLength={1500}
                  value={form.bio}
                  onChange={(e) => set("bio", e.target.value)}
                  className="mt-1.5"
                />
              </div>

              {errorEnvio && <p className="text-sm text-destructive">{errorEnvio}</p>}

              <Button type="submit" size="lg" className="w-full" disabled={enviando}>
                {enviando ? "Enviando..." : "Enviar solicitud"}
              </Button>
            </form>
          )}
        </section>

        <p className="mt-12 text-center text-sm text-muted-foreground leading-relaxed">
          Revisamos cada solicitud a mano antes de publicar el perfil. Te escribimos en unos días.
        </p>
      </main>

      <Footer />
    </div>
  );
};

export default SoyTerapeuta;
